import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { API, errorText } from '../governance_v2/constants';

export const INV_META = {
  en_curso: { label: 'Investigando…', cls: 'bg-violet-100 text-violet-800 animate-pulse' },
  completada: { label: 'Investigación lista', cls: 'bg-violet-600 text-white' },
  error: { label: 'Investigación con error', cls: 'bg-red-100 text-red-800' },
};

const strip = (html) => (html || '').replace(/^```html|```$/g, '');

export const InvestigationDialog = ({ companyId, signal, isAdmin, onClose }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['investigation', signal.id, signal.investigation?.thread_id],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/materiality/signals/${signal.id}/investigation`)).data,
    refetchInterval: (q) => (q.state.data?.investigation?.status === 'en_curso' ? 8000 : false),
  });
  const again = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/materiality/signals/${signal.id}/investigate`),
    onSuccess: () => { toast.success('Nueva investigación en cola'); qc.invalidateQueries({ queryKey: ['materiality'] }); onClose(); },
    onError: (e) => toast.error(errorText(e)),
  });
  const inv = data?.investigation || signal.investigation;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="investigation-dialog">
        <DialogHeader><DialogTitle>Investigación autónoma · {signal.title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <Badge className={INV_META[inv?.status]?.cls} data-testid="investigation-status">{INV_META[inv?.status]?.label}</Badge>
          <span>Iniciada {inv?.started_at && new Date(inv.started_at).toLocaleString()} · {inv?.mode === 'gobernanza' ? 'SUPER-100 + gobernanza y especialistas' : 'SUPER-100 por módulos'}</span>
          {isAdmin && inv?.status !== 'en_curso' && <Button size="sm" variant="outline" className="ml-auto" onClick={() => again.mutate()} disabled={again.isPending} data-testid="investigation-rerun-btn">
            <RefreshCw className="mr-1 h-3.5 w-3.5" />Investigar de nuevo</Button>}
        </div>
        {isLoading && <p className="text-sm text-slate-400">Cargando…</p>}
        {inv?.status === 'en_curso' && <p className="text-sm text-slate-500">SUPER-100 está analizando la señal. Esta ventana se actualiza sola.</p>}
        {inv?.status === 'error' && <p className="text-sm text-red-600" data-testid="investigation-error">{inv.error || data?.thread?.error_message}</p>}
        {(data?.insights || []).map((i) => (
          <article key={i.id} className="prose prose-sm max-w-none rounded-lg border border-slate-200 p-4 dark:prose-invert dark:border-slate-700" data-testid={`investigation-insight-${i.id}`}>
            <p className="not-prose mb-2 text-xs font-semibold text-violet-600">{i.agent_name}</p>
            <div dangerouslySetInnerHTML={{ __html: strip(i.content) }} />
          </article>))}
      </DialogContent>
    </Dialog>
  );
};
